import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { useCallback, useMemo, useRef } from 'react';
import { walletRepository } from '../repositories/WalletRepository';
import { setNWCConnectionString, setBalance, setLnurl, setCurrencyType, CurrencyType, setStatus, WalletStatus, setStaticAddress, setWalletType, setInfos, setShouldShowSeed, setStats } from '../store/slices/walletSlice';
import { apiResponseToTransaction, sparkResponseToTransaction, Transaction, WalletType } from '../models/Transactions';
import init, { connect, initLogging, LogEntry, Payment, SdkEvent, Seed } from '@breeztech/breez-sdk-spark'
import * as bip39 from 'bip39';
import { buildConnectConfig } from './buildConnectConfig';
import { exchangeRateRepository } from '../repositories/ExchangeRateRepository';
import { getCurrencyCode } from '../utils/Utils';
import { PlebData } from '../models/Pleb';
import { getBreezSdk, getIdentityPubkey, setBreezSdk, setIdentityPubkey } from '../utils/breezSdk';
import { sha256 } from 'js-sha256';
import { websocketService } from '../utils/WebSocketService';
import { Credentials } from '../models/Credentials';
import { getTransactionIdFromSparkPayment } from '../utils/BreezSpark';
import { resolveOnchainAddress } from '../utils/onchainAddress';
import {
  getEncryptedMnemonicFromStorage,
  setEncryptedMnemonicToStorage,
  clearEncryptedMnemonicFromStorage,
} from '../utils/mnemonicStorage';

declare global {
  interface Window {
    newrelic?: {
      noticeError?: (error: Error, customAttributes?: Record<string, string>) => void;
    };
  }
}

const breezSdkLogger = {
  log: (entry: LogEntry): void => {
    console.log(`[breez-sdk][${entry.level}] ${entry.line}`);
  },
};

export function useWallet() {
  const isUnsettingWalletRef = useRef(false);
  const onPaymentSucceededCallbackRef = useRef<((payment: Transaction) => void) | null>(null);
  const onSyncedCallbackRef = useRef<(() => void) | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const wallet = useSelector((state: RootState) => state.wallet);

  const currencyCodeFiat = useMemo(() => {
    return getCurrencyCode();
  }, []);

  const balanceFiat = useMemo(() => {
    if (wallet.balance === null) {
      return null;
    }

    const exchangeRateForSat = exchangeRateRepository.getCachedExchangeRateForSat(currencyCodeFiat);
    if (exchangeRateForSat === null) {
      return null;
    }

    return wallet.balance * exchangeRateForSat;
  }, [wallet.balance, currencyCodeFiat]);

  const regenerateNWCConnectionString = useCallback(async () => {
    walletRepository.regenerateSecretNWC()
      .then((nwcConnectionString) => {
        dispatch(setNWCConnectionString(nwcConnectionString));
      });
  }, [dispatch]);

  const refreshBalance = useCallback(async () => {
    if (isUnsettingWalletRef.current) {
      return;
    }

    if(wallet.walletType === WalletType.SPARK) {
      try {
        const info = await getBreezSdk()?.getInfo({});
        if(info) {
          dispatch(setBalance(info.balanceSats));

          const identityPubkey = getIdentityPubkey();
          if(identityPubkey) {
            walletRepository.updateSparkBalance(identityPubkey, info.balanceSats)
              .catch((error) => {
                console.error('Error updating balance on server:', error);
              });

            walletRepository.getStatsSparkWallet(identityPubkey)
              .then((stats) => {
                if (stats) {
                  dispatch(setStats(stats));
                }
              })
              .catch((error) => {
                console.error('Error getting stats:', error);
              });
          }
        }
      } catch (error) {
        console.error('Error refreshing balance:', error);
      }
    } else if(wallet.walletType === WalletType.INTERNAL) {
      const walletInfos = await walletRepository.getWallet()
      dispatch(setInfos(walletInfos));
    }
  }, [dispatch, wallet.walletType]);

  const setOnPaymentSucceededCallback = useCallback((callback: ((payment: Transaction) => void) | null) => {
    onPaymentSucceededCallbackRef.current = callback;
  }, []);

  const setOnSyncedCallback = useCallback((callback: (() => void) | null) => {
    onSyncedCallbackRef.current = callback;
  }, []);

  const createInvoice = useCallback(async (amount: number): Promise<{invoice: string, paymentHash: string | undefined} | null> => {
    switch(wallet.walletType) {
      case WalletType.SPARK:
        const invoice = await createInvoiceSpark(amount);
        return {
          invoice: invoice,
          paymentHash: undefined,
        }
      case WalletType.INTERNAL:
        const internalInvoice = await createInvoiceInternal(amount);
        return {
          invoice: internalInvoice.invoice,
          paymentHash: internalInvoice?.paymentHash,
        }
    }

    throw new Error('Invalid wallet type');
  }, [wallet.walletType]);

  const createInvoiceSpark = useCallback(async (amount: number): Promise<string> => {
    const breezSdk = getBreezSdk();
    if(!breezSdk) {
        throw new Error('Breez SDK not found');
    }
    const invoice = await breezSdk.receivePayment({
        paymentMethod: {
            type: 'bolt11Invoice',
            description: "",
            amountSats: amount,
            expirySecs: 7 * 24 * 60 * 60,
            paymentHash: undefined
        }
    })
    return invoice.paymentRequest;
  }, []);

  const createInvoiceInternal = useCallback(async (amount: number): Promise<{invoice: string, paymentHash: string}> => {
    const invoice = await walletRepository.generateInvoice(amount * 1000);
    return {
      invoice: invoice.bolt11,
      paymentHash: invoice.paymentHash,
    };
  }, []);

  const getOnchainAddress = useCallback(async (): Promise<string | null> => {
    if (wallet.walletType !== WalletType.SPARK) {
      return null;
    }

    return resolveOnchainAddress(getIdentityPubkey());
  }, [wallet.walletType]);

  const initInternalWallet = useCallback(async (pleb: PlebData, credentials: Credentials) => {
    dispatch(setWalletType(WalletType.INTERNAL));
    const walletInfos = await walletRepository.getWallet()
    dispatch(setInfos(walletInfos));
    dispatch(setStatus(WalletStatus.ACTIVE));

    const socket = websocketService.connect(credentials.accessToken);

    socket.on('wallet.receive', ({newBalance, payment}: {newBalance: number, payment: Transaction}) => {
      dispatch(setBalance(newBalance));
      if (onPaymentSucceededCallbackRef.current) {
        onPaymentSucceededCallbackRef.current(apiResponseToTransaction(payment));
      }
    });
  }, [dispatch, refreshBalance]);

  const initSparkWallet = useCallback(async (pleb: PlebData) => {
    try {
      dispatch(setStatus(WalletStatus.SYNCING));
      await init();
      // await initLogging(breezSdkLogger);

      const seed = await getEncryptedMnemonicFromStorage(pleb.id);

      if(seed) {
        await connectToSparkWallet(pleb, seed);
      } else {
        const activeWallet = await walletRepository.getActiveSparkWallet();
        if (activeWallet) {
          dispatch(setStatus(WalletStatus.OTHER_WALLET_ACTIVE));
          return;
        }

        await createNewWallet(pleb);
      }
    } catch (error) {
      window.newrelic?.noticeError?.(
        error instanceof Error ? error : new Error('Error initializing wallet'),
        { context: 'useWallet.initSparkWallet' },
      );
      dispatch(setStatus(WalletStatus.ERROR));
    }
  }, [dispatch]);

  const restoreWallet = useCallback(async (pleb: PlebData, seed: Seed) => {
    await setEncryptedMnemonicToStorage(pleb.id, seed.type === 'mnemonic' ? seed.mnemonic : '');
    await connectToSparkWallet(pleb, seed);
  }, []);

  const createNewWallet = useCallback(async (pleb: PlebData) => {
    const seed = await generateMnemonic(pleb.id);
    await connectToSparkWallet(pleb, seed);
  }, []);

  const connectToSparkWallet = useCallback(async (pleb: PlebData, seed: Seed) => {
    dispatch(setStatus(WalletStatus.SYNCING));

    const cfg = buildConnectConfig();

    const hashedSeed = hashSeed(seed);

    const connectedSdk = await connect({
      config: cfg,
      seed,
      storageDir: 'spark-wallet-' + hashedSeed,
    });
    setBreezSdk(connectedSdk);

    const [info, receivePayment] = await Promise.all([
      connectedSdk.getInfo({ensureSynced: true}),
      connectedSdk.receivePayment({
        paymentMethod: { type: 'sparkAddress' }
      }),
    ]);
    const staticAddress = receivePayment.paymentRequest;
    setIdentityPubkey(info.identityPubkey);

    walletRepository.getStatsSparkWallet(info.identityPubkey)
      .then((stats) => {
        if (stats) {
          dispatch(setStats(stats));
        }
      })
      .catch((error) => {
        console.error('Error getting stats:', error);
      });

    dispatch(setWalletType(WalletType.SPARK));

    const {wallet, showSaveSeedButton} = await walletRepository.registerSparkWallet(info.identityPubkey, staticAddress, info.balanceSats);

    try {
      if(wallet.id) {
        if(!wallet.lnAddress) {
          const addressInfo = await connectedSdk.registerLightningAddress({
            username: wallet.id,
            description: "Pay on Club Orange"
          })
          if(addressInfo.lightningAddress) {
            await walletRepository.saveSparkLnAddress(info.identityPubkey, addressInfo.lightningAddress);
          }
        }

        walletRepository.initSparkWallet()
          .catch((error) => {
            console.error('Error migrating balance to spark:', error);
          });
      }
    } catch (error) {
      console.error('Error registering lightning address:', error);
    }

    dispatch(setInfos({
      balance: info.balanceSats,
      lnurl: pleb.zapAddress ?? '',
      staticAddress: staticAddress,
      shouldShowSeed: showSaveSeedButton,
      identityPubkey: info.identityPubkey,
    }))

    dispatch(setStatus(WalletStatus.ACTIVE));

    connectedSdk.addEventListener({
      onEvent: (event: SdkEvent) => {
        if (isUnsettingWalletRef.current) {
          return;
        }
        switch(event.type) {
          case 'paymentSucceeded':
            refreshBalance();
            if (onPaymentSucceededCallbackRef.current) {
              const breezPayment = event.payment as Payment;
              const transactionId = getTransactionIdFromSparkPayment(breezPayment);
              onPaymentSucceededCallbackRef.current(
                sparkResponseToTransaction({
                  payment: breezPayment, id: transactionId
                })
              );
            }
            break;
          case 'synced':
            refreshBalance();
            if (onSyncedCallbackRef.current) {
              onSyncedCallbackRef.current();
            }
            break;
        }
      }
    });

    if (onSyncedCallbackRef.current) {
      onSyncedCallbackRef.current();
    }
  }, [refreshBalance]);

  const unsetWallet = useCallback(() => {
    isUnsettingWalletRef.current = true;
    websocketService.disconnect();
    const sdk = getBreezSdk();
    setBreezSdk(null);
    setIdentityPubkey(undefined);
    sdk?.disconnect();
    queueMicrotask(() => {
      isUnsettingWalletRef.current = false;
    });
    dispatch(setStatus(WalletStatus.LOADING));
  }, [dispatch]);

  const deleteAndCreateNewWallet = useCallback((pleb: PlebData) => {
    clearEncryptedMnemonicFromStorage(pleb.id);
    unsetWallet();
    createNewWallet(pleb);
  }, [createNewWallet, unsetWallet]);

  const deleteWallet = useCallback((pleb: PlebData) => {
    clearEncryptedMnemonicFromStorage(pleb.id);
    unsetWallet();
    dispatch(setStatus(WalletStatus.OTHER_WALLET_ACTIVE));
  }, [unsetWallet]);

  return {
    status: wallet.status,
    walletType: wallet.walletType,
    balance: wallet.balance,
    lnurl: wallet.lnurl,
    staticAddress: wallet.staticAddress,
    lockedFunds: wallet.lockedFunds,
    nwcConnectionString: wallet.nwcConnectionString,
    currencyType: wallet.currencyType,
    shouldShowSeed: wallet.shouldShowSeed,
    identityPubkey: wallet.identityPubkey,
    stats: wallet.stats,
    setShouldShowSeed: useCallback((shouldShowSeed: boolean) => dispatch(setShouldShowSeed(shouldShowSeed)), [dispatch]),
    balanceFiat,
    currencyCodeFiat,
    setCurrencyType: useCallback((currencyType: CurrencyType) => 
      dispatch(setCurrencyType(currencyType)), [dispatch]),
    createInvoice,
    getOnchainAddress,
    refreshBalance,
    setOnPaymentSucceededCallback,
    setOnSyncedCallback,
    regenerateNWCConnectionString,
    initSparkWallet: initSparkWallet,
    initInternalWallet: initInternalWallet,
    createNewWallet: createNewWallet,
    restoreWallet: restoreWallet,
    unsetWallet: unsetWallet,
    deleteAndCreateNewWallet: deleteAndCreateNewWallet,
    deleteWallet: deleteWallet,
    breezSdk: getBreezSdk()
  };
} 

const generateMnemonic = async (userId: string): Promise<Seed> => {
  const newMnemonic = bip39.generateMnemonic(128);
  await setEncryptedMnemonicToStorage(userId, newMnemonic);
  return { type: 'mnemonic', mnemonic: newMnemonic };
};

export {
  getEncryptedMnemonicFromStorage,
  clearEncryptedMnemonicFromStorage,
} from '../utils/mnemonicStorage';

const hashSeed = (seed: Seed): string => {
  return sha256(seed.type === 'mnemonic' ? seed.mnemonic : '');
}